import React, { useEffect, useRef, useState } from "react";
import { Search, X, User } from "lucide-react";
import { adminApi } from "../../lib/api";

/**
 * Reusable customer typeahead — for Bundle & Send, Notify Client, and the
 * New Invoice screen. `value` is the currently-selected customer object
 * (or null). `onChange(customer)` fires with the picked object or null.
 */
export const CustomerCombobox = ({
  value,
  onChange,
  placeholder = "Search customers by name or code…",
  testid = "customer-combobox",
  autoFocus = false,
}) => {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!q || q.length < 2) { setRows([]); return; }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const data = await adminApi.listCustomers({ q, active: true });
        if (!cancelled) setRows(Array.isArray(data) ? data.slice(0, 12) : []);
      } catch {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 220);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q]);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const pick = (c) => { onChange(c); setQ(""); setRows([]); setOpen(false); };

  if (value) {
    return (
      <div className="flex items-center gap-2 border border-[#cfd7db] rounded-md p-2 bg-[#f5f6f6]" data-testid={`${testid}-selected`}>
        <User size={14} className="text-[#f28a16]" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-bold text-[#111619] truncate">
            {value.name}
            {value.code && <span className="ml-2 text-xs font-display uppercase tracking-widest text-[#3f4b52]">{value.code}</span>}
          </div>
          <div className="text-[11px] text-[#3f4b52] truncate">
            {[value.email, value.mobile].filter(Boolean).join(" · ") || "no contact on file"}
          </div>
        </div>
        <button type="button" onClick={() => onChange(null)} className="text-[#3f4b52] hover:text-[#dc2626]" data-testid={`${testid}-clear`} aria-label="Clear customer">
          <X size={14} />
        </button>
      </div>
    );
  }

  return (
    <div className="relative" ref={boxRef}>
      <div className="flex items-center gap-2 border border-[#cfd7db] rounded-md h-10 px-3 bg-white">
        <Search size={14} className="text-[#3f4b52] shrink-0" />
        <input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          autoFocus={autoFocus}
          className="flex-1 h-full outline-none text-sm bg-transparent"
          data-testid={`${testid}-input`}
        />
      </div>
      {open && q.length >= 2 && (
        <div className="absolute z-40 mt-1 w-full max-h-72 overflow-y-auto bg-white border border-[#cfd7db] rounded-md shadow-lg" data-testid={`${testid}-dropdown`}>
          {loading && <div className="px-3 py-2 text-xs text-[#3f4b52]">Searching…</div>}
          {!loading && rows.length === 0 && (
            <div className="px-3 py-2 text-xs text-[#3f4b52]" data-testid={`${testid}-empty`}>No customers match &quot;{q}&quot;</div>
          )}
          {rows.map((c) => (
            <button
              type="button"
              key={c.id}
              onClick={() => pick(c)}
              className="w-full text-left px-3 py-2 hover:bg-[#f5f6f6] flex items-center gap-2 border-b border-[#f5f6f6] last:border-b-0"
              data-testid={`${testid}-option-${c.code || c.id}`}
            >
              <User size={13} className="text-[#f28a16] shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-bold text-[#111619] truncate">
                  {c.name}
                  {c.code && <span className="ml-2 text-xs font-display uppercase tracking-widest text-[#3f4b52]">{c.code}</span>}
                </div>
                <div className="text-[11px] text-[#3f4b52] truncate">
                  {[c.email, c.mobile].filter(Boolean).join(" · ") || "no contact on file"}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default CustomerCombobox;
